import { energyOsClient } from './client'
import type { ServiceConfigParam, EnergyOsAuditLog, EnergyOsBootstrapStatus } from '../types'

export const getEnergyOsConfig = () =>
  energyOsClient.get<ServiceConfigParam[]>('/config')

export const setEnergyOsConfig = (key: string, value: string) =>
  energyOsClient.post('/config', { [key]: value })

export const getEnergyOsBootstrapStatus = () =>
  energyOsClient.get<EnergyOsBootstrapStatus>('/bootstrap-status')

export const triggerEnergyOsBootstrap = () =>
  energyOsClient.post<EnergyOsBootstrapStatus>('/bootstrap')

export const getEnergyOsAuditLogs = (params?: { action?: string; homeId?: string; deviceId?: string; limit?: number }) =>
  energyOsClient.get<EnergyOsAuditLog[]>('/audit-logs', { params })

// ── Homeowner self-service ("me") — Phase 6 ─────────────────────────────────────
// Ownership is always re-derived server-side from the caller's own JWT, so these never take a
// home/device id from the caller.

// One unified device concept (Phase 5d-2): every device — simulated/controllable
// (vendor:'mock', connectionMode:'control') and real, ingest-only (Shelly plugs,
// push/pull/upload/websocket) alike — is added the same way: deviceType, subType, name,
// allowControls.
export interface MyDevice {
  id: string
  deviceCoffeeId: string
  deviceType: string
  subType?: string
  label: string
  vendor: string
  connectionMode: string
  externalId?: string
  controllable: boolean
  capacityKwh?: number
  ratedPowerKw?: number
  simulationEnabled: boolean
  createdAt: string
}

export interface MyHome {
  homeCoffeeId: string
  mpid: string | null
  country: string | null
  devices: MyDevice[]
}

export const getMyHome = () =>
  energyOsClient.get<MyHome>('/me/home')

export const createMyHome = (data: { mpid: string; country: string }) =>
  energyOsClient.post<{ homeCoffeeId: string; deviceCoffeeId: string }>('/me/home', data)

export interface MyHomeOverview {
  mpid: string | null
  deviceCount: number
  solarPowerKw?: number
  batterySocPercent?: number
  meterPowerKw?: number
}

export interface MyPlanAction {
  id: string
  deviceId?: string
  actionType?: string
  startTime?: string
  endTime?: string
  targetValue?: string
  executionStatus?: string
}

export interface MyTodaysPlan {
  plan: { id: string; generatedAt?: string; status?: string } | null
  actions: MyPlanAction[]
}

export interface MyEvent {
  id: string
  eventType: string
  startTime?: string
  endTime?: string
  note?: string
}

export const getMyHomeOverview = () =>
  energyOsClient.get<MyHomeOverview>('/me/home/overview')

export const getMyHomePlan = () =>
  energyOsClient.get<MyTodaysPlan>('/me/home/plan')

export const getMyHomeForecast = () =>
  energyOsClient.get<Record<string, number>>('/me/home/forecast')

export const getMyHomeEvents = () =>
  energyOsClient.get<MyEvent[]>('/me/home/events')

export const addMyHomeEvent = (data: { eventType: string; startTime?: string; endTime?: string; note?: string }) =>
  energyOsClient.post<{ id: string }>('/me/home/events', data)

export const stopMyHome = () =>
  energyOsClient.post<{ ok: boolean; pausedUntil: string }>('/me/home/stop')

// ── Devices — Bronze/Silver/Gold pipeline, merged with the former mock model (5d/5d-2) ──────

export interface MyDeviceRegistration {
  deviceCoffeeId: string
  ingestToken?: string
}

// segmentLabel/segmentColor (Phase 9) — which contract price segment (time-of-day period,
// consumption tier, or dynamic price point) applied at this reading, server-computed from the
// home's active contract. Absent when there's no contract, or for a non-power metric.
export interface MyDeviceMetric {
  timestamp: string
  metric: string
  value: number
  unit: string
  segmentLabel?: string
  segmentColor?: string
}

export interface MyDeviceCost {
  periodStart: string
  periodEnd: string
  metricType: string
  value: number
  unit: string
  segmentLabel?: string
  segmentColor?: string
}

export interface MyRawReading {
  receivedAt: string
  sourceTimestamp?: string
  rawPayload: string
  ingestMode: string
}

// Data-driven vendor registry (Phase 7) — replaces a hardcoded frontend list so new vendors can
// be added (bootstrap.service.ts's VENDOR_SEEDS) without a frontend deploy.
export interface MyVendor {
  name: string
  label: string
  supportsPolling: boolean
  supportsFileUpload: boolean
  uploadEventTitle: string | null
  requiredCountry: string | null
}

export const getMyVendors = () =>
  energyOsClient.get<MyVendor[]>('/me/vendors')

// ── Contracts & pricing (Phase 8) ────────────────────────────────────────────

export interface MyProvider {
  name: string
  country: string
}

export const getMyProviders = () =>
  energyOsClient.get<MyProvider[]>('/me/providers')

export interface Fee { name: string; amount: number }
export interface TimeOfDayPeriod { name: string; startTime: string; endTime: string; pricePerKwh: number }
export interface PriceTier { fromKwh: number; toKwh: number | null; pricePerKwh: number }

export type PricingRule =
  | { type: 'timeOfDay'; monthlyFees: Fee[]; periods: TimeOfDayPeriod[] }
  | { type: 'incremental'; monthlyFees: Fee[]; tiers: PriceTier[] }
  | { type: 'threshold'; monthlyFees: Fee[]; tiers: PriceTier[] }
  | { type: 'dynamic'; monthlyFees: Fee[] }

export interface MyContract {
  id: string
  dateStart: string
  dateEnd: string | null
  retailerName: string
  buyPricingRule: PricingRule
  sellPricingRule: PricingRule | null
}

export const getMyContracts = () =>
  energyOsClient.get<MyContract[]>('/me/home/contracts')

export const createMyContract = (data: { dateStart: string; dateEnd?: string | null; retailerName: string; buyPricingRule: PricingRule; sellPricingRule?: PricingRule | null }) =>
  energyOsClient.post<MyContract>('/me/home/contracts', data)

export const updateMyContract = (contractId: string, data: Partial<{ dateStart: string; dateEnd: string | null; retailerName: string; buyPricingRule: PricingRule; sellPricingRule: PricingRule | null }>) =>
  energyOsClient.patch<MyContract>(`/me/home/contracts/${contractId}`, data)

export const deleteMyContract = (contractId: string) =>
  energyOsClient.delete(`/me/home/contracts/${contractId}`)

export interface MyDynamicPricePoint {
  id: string
  chapter: 'buy' | 'sell'
  effectiveAt: string
  pricePerKwh: number
}

export const getMyContractPricePoints = (contractId: string) =>
  energyOsClient.get<MyDynamicPricePoint[]>(`/me/home/contracts/${contractId}/price-points`)

export const addMyContractPricePoint = (contractId: string, data: { chapter: 'buy' | 'sell'; effectiveAt: string; pricePerKwh: number }) =>
  energyOsClient.post<MyDynamicPricePoint>(`/me/home/contracts/${contractId}/price-points`, data)

// ── Invoices (Phase 9) ───────────────────────────────────────────────────────
// `past` is persisted (one immutable snapshot per closed calendar month, last 12); `current` is
// always computed live against the still-accumulating month and never stored — null when the
// home has no active contract or no designated meter yet.

export interface MyInvoice {
  id: string
  periodStart: string
  periodEnd: string
  consumptionKwh: number
  consumptionCost: number
  monthlyFeesCost: number
  totalCost: number
  generatedAt: string
}

export interface MyInvoiceCurrent {
  periodStart: string
  consumptionKwhSoFar: number
  consumptionCostSoFar: number
  monthlyFeesCost: number
  estimatedTotal: number
}

export const getMyInvoices = () =>
  energyOsClient.get<{ past: MyInvoice[]; current: MyInvoiceCurrent | null }>('/me/home/invoices')

export const addMyDevice = (data: {
  deviceType: string; subType?: string; label: string; vendor: string; connectionMode?: 'push' | 'pull' | 'upload' | 'websocket' | 'control';
  controllable?: boolean; capacityKwh?: number; ratedPowerKw?: number; externalId?: string;
}) =>
  energyOsClient.post<MyDeviceRegistration>('/me/home/devices', data)

export const updateMyDevice = (deviceId: string, data: { label?: string; subType?: string; controllable?: boolean; vendor?: string; simulationEnabled?: boolean }) =>
  energyOsClient.patch<MyDevice>(`/me/home/devices/${deviceId}`, data)

export const getMyDeviceMetrics = (deviceId: string) =>
  energyOsClient.get<MyDeviceMetric[]>(`/me/home/devices/${deviceId}/metrics`)

export const getMyDeviceCost = (deviceId: string) =>
  energyOsClient.get<MyDeviceCost[]>(`/me/home/devices/${deviceId}/cost`)

export const getMyDeviceRawReadings = (deviceId: string) =>
  energyOsClient.get<MyRawReading[]>(`/me/home/devices/${deviceId}/raw`)

// Generic file upload (Phase 7's Enedis Excel connector is the first, not the only, consumer) —
// which vendors even support this, and what happens on upload, is read server-side from the
// device's own vendor's MyVendor config (see rest/routes.ts's file-upload route), not hardcoded.
export const uploadMyDeviceFile = (deviceId: string, file: File) => {
  const formData = new FormData()
  formData.append('file', file)
  return energyOsClient.post<{ documentId: string }>(`/me/home/devices/${deviceId}/file-upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// Irreversible — server requires { confirm: true } as a backstop behind the frontend's own
// confirm dialog. Fire-and-forget: a large history is paced server-side (1200ms/row) and can take
// a while, so this only confirms the erase started, not its outcome (see reprocessMyDeviceHistory).
export const eraseMyDeviceHistory = (deviceId: string) =>
  energyOsClient.delete<{ started: boolean }>(`/me/home/devices/${deviceId}/history`, { data: { confirm: true } })

// Recovers Silver Metrics that never got written from existing Bronze RawReadings (e.g. a
// device's vendor had no adapter yet at upload time, or a bulk transform was rate-limited
// partway) — re-parses the device's full RawReading history, skipping anything already present.
// Fire-and-forget: a large history is paced server-side (1200ms/row) and can take a while, so
// this only confirms the reprocess started, not its outcome.
export const reprocessMyDeviceHistory = (deviceId: string) =>
  energyOsClient.post<{ started: boolean }>(`/me/home/devices/${deviceId}/reprocess-history`)
